import { Outlet, useLocation } from "react-router";
import useLivlyUser from "@/context/UserProvider";
import { PermissionAccessLevelEnum, ServiceTypeEnum } from "@/types/Building";
import ServiceNotAvailable from "./ServiceNotAvailable";
import { useChecklist } from "@/context/ChecklistProvider";
import { OnboardingStatusEnum } from "@/types/User";

interface RequireServiceProps {
  serviceType: ServiceTypeEnum;
  serviceName?: string;
  onboardingServiceType?: OnboardingStatusEnum;
  children: React.ReactNode;
}

/**
 * Route guard that verifies a building service is enabled and the user
 * has permission before rendering children. Shows a "not available" screen
 * when the service is disabled or access is denied.
 */
export default function RequireService({
  serviceType,
  serviceName,
  onboardingServiceType,
  children
}: RequireServiceProps) {
  const { user } = useLivlyUser();
  const data = useChecklist();
  const location = useLocation();
  const isChecklist = location.pathname.includes("/checklist/");

  if (isChecklist && data.onboardingStatuses?.length) {
    const service = data.onboardingStatuses?.find(
      (s) => s.statusTypeId === onboardingServiceType
    );

    if (!service || !service.enabled) {
      return <ServiceNotAvailable serviceName={serviceName ?? service?.name} />;
    }
  }

  const service = user.building.serviceTypes?.find(
    (s) => s.serviceType === serviceType
  );
  if (
    !service ||
    !service.enabled ||
    service.permissionAccessLevel === PermissionAccessLevelEnum.NoAccess
  ) {
    return (
      <ServiceNotAvailable serviceName={serviceName ?? service?.displayName} />
    );
  }

  return <>{children}</>;
}

/**
 * Layout-route version of RequireService that renders an <Outlet /> instead
 * of children. Use as a pathless wrapper route element.
 */
export function RequireServiceOutlet({
  serviceType,
  serviceName
}: Omit<RequireServiceProps, "children">) {
  const { user } = useLivlyUser();

  const service = user.building.serviceTypes?.find(
    (s) => s.serviceType === serviceType
  );

  if (
    !service ||
    !service.enabled ||
    service.permissionAccessLevel === PermissionAccessLevelEnum.NoAccess
  ) {
    return (
      <ServiceNotAvailable serviceName={serviceName ?? service?.displayName} />
    );
  }

  return <Outlet />;
}
